/**
 * Adaptive resolution, decided once a second. Sharpness is traded only while the game is genuinely
 * slow (< ~50 fps). Above that, the resolution goes only when the GPU itself can't keep the display
 * rate — measured, so a CPU-bound frame never costs pixels — and never below a sharp floor, and the
 * effect density is kept. With a GPU timer the resolution also climbs back (the caller lets it pass
 * the boot pixel budget) while the GPU has clear headroom at the display rate.
 */
export interface AdaptInput {
  /** Smoothed frame time / display interval (1 = on budget). */
  ratio: number;
  /** Display interval, seconds. */
  refresh: number;
  /** Smoothed GPU ms per frame; NaN without a timer. */
  gpuMs: number;
  dpr: number; minDpr: number; maxDpr: number;
  /** The display's own density (the sharp floor is min(it, 1.25)). */
  deviceDpr: number;
  density: number; maxDensity: number;
  /** ms since the last downgrade. */
  sinceDrop: number;
}
export interface AdaptOutput { dpr: number; density: number; dropped: boolean }

export function adapt(i: AdaptInput): AdaptOutput {
  const frame = i.ratio * i.refresh, measured = Number.isFinite(i.gpuMs), slow = frame > 1 / 50, budget = i.refresh * 1000;
  const gpuBound = measured ? i.gpuMs > budget * 0.92 && i.ratio > 1.08 : i.ratio > 1.25;
  const floor = slow ? i.minDpr : Math.max(i.minDpr, Math.min(i.deviceDpr, 1.25));
  if (slow || gpuBound) {
    // resolution only helps a GPU-bound frame; when slow for another reason (or at the floor), effects thin out
    if (i.dpr > floor + 0.01 && (gpuBound || !measured)) return { dpr: Math.max(floor, i.dpr - (slow ? 0.25 : 0.125)), density: i.density, dropped: true };
    if (slow) return { dpr: i.dpr, density: Math.max(0.3, i.density * 0.8), dropped: true };
    return { dpr: i.dpr, density: i.density, dropped: false };
  }
  if (i.sinceDrop > 6000) {
    if (i.density < i.maxDensity) return { dpr: i.dpr, density: Math.min(i.maxDensity, i.density + 0.1), dropped: false };
    const room = measured ? i.gpuMs < budget * 0.68 : i.ratio < 1.04;
    if (room && i.dpr < i.maxDpr - 0.01) return { dpr: Math.min(i.maxDpr, i.dpr + 0.125), density: i.density, dropped: false };
  }
  return { dpr: i.dpr, density: i.density, dropped: false };
}
